/* ============================================================================
   AppShell — the authenticated frame.
   - Navy top app bar with role-filtered nav (from getNav()).
   - Routed content area (from getRoutes()), each route role-guarded.
   - Login gate: unauthenticated users see the Login page.

   This is spine. Feature modules never edit this file.
   ============================================================================ */

import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { NavLink, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useAuth, hasRole } from '../lib/auth';
import { getRoutes, getNav, type RouteSpec } from './registry';
import { Wordmark } from './Brand';
import Login from '../modules/auth/Login';
import ChangePasswordDialog from '../modules/auth/ChangePasswordDialog';
import NotificationBell from '../modules/notifications/NotificationBell';

import '../design/tokens.css';
import '../design/base.css';

function TopBar() {
  const { user, logout } = useAuth();
  const nav = getNav().filter((n) => hasRole(user, n.roles));

  // Group nav items by section for a light visual grouping.
  const ungrouped = nav.filter((n) => !n.section);
  const sections = Array.from(new Set(nav.filter((n) => n.section).map((n) => n.section!)));

  return (
    <header
      style={{
        height: 'var(--appbar-h)',
        background: 'var(--uva-navy)',
        color: 'var(--ink-on-navy)',
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        padding: '0 24px',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <NavLink to="/" style={{ textDecoration: 'none' }}>
        <Wordmark />
      </NavLink>

      <nav style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, overflowX: 'auto' }}>
        {ungrouped.map((n) => (
          <NavItemLink key={n.path} to={n.path} label={n.label} icon={n.icon} />
        ))}
        {sections.map((section) => (
          <Fragment key={section}>
            <span
              aria-hidden
              style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.16)', margin: '0 8px' }}
            />
            {nav
              .filter((n) => n.section === section)
              .map((n) => (
                <NavItemLink key={n.path} to={n.path} label={n.label} icon={n.icon} />
              ))}
          </Fragment>
        ))}
      </nav>

      {user && (
        <>
          <NotificationBell />
          <UserMenu name={user.name} role={user.role} onSignOut={logout} />
        </>
      )}
    </header>
  );
}

/**
 * Top-bar user block turned into a small dropdown menu. Styled like the former
 * ghost Sign-out control (navy surface, orange only on focus rings). Opens on
 * click, closes on outside click / Escape, and is keyboard-navigable.
 */
function UserMenu({
  name,
  role,
  onSignOut,
}: {
  name: string;
  role: string;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);

  // Close on outside click + Escape while the menu is open.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Move focus into the menu when it opens (keyboard accessibility).
  useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  const itemStyle: CSSProperties = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '9px 14px',
    background: 'transparent',
    border: 'none',
    color: 'var(--ink)',
    fontSize: 'var(--fs-sm)',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: 'var(--r-sm)',
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 'auto',
          padding: '6px 12px',
          background: open ? 'rgba(255,255,255,0.08)' : 'transparent',
          color: 'var(--ink-on-navy)',
          borderColor: 'rgba(255,255,255,0.24)',
        }}
      >
        <span style={{ textAlign: 'right', lineHeight: 1.2 }}>
          <span style={{ display: 'block', fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{name}</span>
          <span
            style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--ink-on-navy-2)' }}
          >
            {role}
          </span>
        </span>
        <span aria-hidden style={{ fontSize: 10, opacity: 0.8 }}>
          ▾
        </span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className="card"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: 200,
            padding: 6,
            boxShadow: 'var(--shadow-lg)',
            zIndex: 60,
          }}
        >
          <button
            ref={firstItemRef}
            type="button"
            role="menuitem"
            style={itemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(35,45,75,0.06)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            onClick={() => {
              setOpen(false);
              setShowChangePassword(true);
            }}
          >
            Change password
          </button>

          <div
            aria-hidden
            style={{ height: 1, background: 'var(--line)', margin: '5px 8px' }}
          />

          <button
            type="button"
            role="menuitem"
            style={itemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(35,45,75,0.06)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}

      {showChangePassword && (
        <ChangePasswordDialog onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}

function NavItemLink({ to, label, icon }: { to: string; label: string; icon?: string }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 34,
        padding: '0 12px',
        borderRadius: 'var(--r-md)',
        fontWeight: 600,
        fontSize: 'var(--fs-sm)',
        textDecoration: 'none',
        color: isActive ? '#fff' : 'var(--ink-on-navy-2)',
        background: isActive ? 'rgba(229,114,0,0.18)' : 'transparent',
        boxShadow: isActive ? 'inset 0 -2px 0 var(--uva-orange)' : 'none',
        whiteSpace: 'nowrap',
      })}
    >
      {icon && <span aria-hidden>{icon}</span>}
      {label}
    </NavLink>
  );
}

/** Role-guarded route element. */
function Guarded({ spec }: { spec: RouteSpec }) {
  const { user } = useAuth();
  if (!hasRole(user, spec.roles)) {
    return (
      <div className="container page">
        <div className="empty card">
          <h3>Not authorized</h3>
          <p className="muted">You don't have access to this area. Contact an administrator.</p>
        </div>
      </div>
    );
  }
  return spec.element;
}

function DefaultLanding(): ReactElement {
  // Send users to the first nav route they can see, else a friendly empty state.
  const { user } = useAuth();
  const nav = getNav().filter((n) => hasRole(user, n.roles));
  if (nav.length > 0) return <Navigate to={nav[0].path} replace />;
  return (
    <div className="container page">
      <div className="empty card">
        <h3>Welcome to UVA Creative Requests</h3>
        <p className="muted">No modules are available for your role yet.</p>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', background: 'var(--ground)' }}>
        <div className="spinner" />
      </div>
    );
  }

  // Unauthenticated -> only public routes are reachable; everything else -> Login.
  if (!user) {
    const publicRoutes = getRoutes().filter((r) => r.publicRoute);
    return (
      <Routes>
        {publicRoutes.map((r) => (
          <Route key={r.path} path={r.path} element={r.element} />
        ))}
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  const routes = getRoutes().filter((r) => !r.publicRoute);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <TopBar />
      <main style={{ flex: 1 }}>
        <Routes location={location}>
          <Route index element={<DefaultLanding />} />
          {routes.map((r) => (
            <Route key={r.path} path={r.path} element={<Guarded spec={r} />} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
